import { ChangeEvent } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { Node } from '@xyflow/react';
import { uid } from 'uid';

import { store } from 'store/store';

import { setIsUpdate, setUpdateNode } from '../store';

export const useUpdateNode = () => {
  const dispatch = useAppDispatch();
  const nodeId = useAppSelector(state => state.aiAgentReducer.nodeId);
  const nodes = useAppSelector(state => state.aiAgentReducer.nodes);
  const currentNode = nodes.find(item => item?.id === nodeId);

  const getFreshNode = () => {
    const freshNodes = store.getState().aiAgentReducer.nodes;

    return freshNodes.find(item => item.id === nodeId);
  };

  const updateNodesState = (updatedNode: Node) => {
    const prevNode = nodes.find(item => item.id === updatedNode.id);
    const hasChanged = JSON.stringify(prevNode?.data) !== JSON.stringify(updatedNode?.data);

    const updatedNodes = [...nodes.filter(item => item.id !== nodeId), updatedNode];

    dispatch(setUpdateNode(updatedNodes));

    if (hasChanged) {
      dispatch(setIsUpdate(true));
    }
  };

  const updateDescription = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const node = getFreshNode();

    if (!node) return;

    const updatedNode: Node = {
      ...node,
      data: {
        ...node.data,
        description: e.target.value
      }
    };

    updateNodesState(updatedNode);
  };

  const updateName = (e: ChangeEvent<HTMLInputElement>) => {
    const node = getFreshNode();

    if (!node) return;
    const updatedNode: Node = {
      ...node,
      data: {
        ...node.data,
        name: e.target.value
      }
    };

    updateNodesState(updatedNode);
  };

  const updateJsonField = (field: string, value: any, parentField?: string) => {
    const node = getFreshNode();

    if (!node) return;

    const updatedNode: Node = {
      ...node,
      data: {
        ...node.data,
        json: {
          ...(node.data?.json || {}),
          ...(parentField
            ? { [parentField]: { ...(node?.data?.json as any)?.[parentField], [field]: value || '' } }
            : { [field]: value })
        }
      }
    };

    updateNodesState(updatedNode);
  };

  const updateOptionField = (index: number, field: 'name' | 'description', value: string) => {
    const node = getFreshNode();

    if (!node) return;

    const options = Array.isArray(node.data?.options) ? node.data.options : [];
    const updatedOptions = [...options];

    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value
    };

    const updatedNode: Node = {
      ...node,
      data: {
        ...node.data,
        options: updatedOptions
      }
    };

    updateNodesState(updatedNode);
  };

  const addOptionItem = () => {
    const node = getFreshNode();

    if (!node) return;

    const currentOptions = (node.data?.options as { oldId: string; name: string; description: string }[]) || [];

    const newOption = {
      oldId: `${uid()}_new`,
      name: `Class ${currentOptions?.length + 1}`,
      description: ''
    };

    const updatedNode: Node = {
      ...node,
      data: {
        ...node.data,
        options: [...currentOptions, newOption]
      }
    };

    updateNodesState(updatedNode);
  };

  const removeOptionItem = (index: number) => {
    const node = getFreshNode();

    if (!node) return;

    const currentOptions = (node.data?.options as { oldId: string; name: string; description: string }[]) || [];

    const updatedOptions = currentOptions.filter((_, i) => i !== index);

    const updatedNode: Node = {
      ...node,
      data: {
        ...node.data,
        options: updatedOptions
      }
    };

    updateNodesState(updatedNode);
  };

  return {
    currentNode,
    updateDescription,
    updateName,
    updateJsonField,
    updateOptionField,
    addOptionItem,
    removeOptionItem
  };
};
